import Head from 'next/head';
import { stripHtml } from 'string-strip-html';
import BlogBody from 'components/Blog/BlogBody';
import BlogHeader from 'components/Blog/BlogHeader';
import BlogBottom from 'components/Blog/BlogBottom';
import Shell from 'components/Shell/Shell';
import StyleContextProvider from 'utils/context/StyleContextProvider';
import ArticleSchema from 'utils/schema/Article';
import { Contentful, getMasterControl } from '@viome/vnxt-nucleus';
import Breadcrumb from 'components/Tailwind/Components/Breadcrumb';
import ProgressBar from 'components/Tailwind/Components/ProgressBar';
import {
    contentfulOptions as options,
    VLS_BLOG_CATEGORIES,
} from 'utils/constants';
import styles from './blog.module.scss';
import ErrorPage from '../404';

const Blog = (props) => {
    const { preview, blog } = props,
        metaDescription = stripHtml(blog?.metaDescription || '').result,
        articleCategories = blog?.categories,
        articleCategory = articleCategories && articleCategories[0],
        articleDate = blog?.date,
        categoryTitle = articleCategory?.title,
        masterControl = props && props.masterControl;

    // change articleCategory.title replace "Research:" to ""
    if (articleCategory && articleCategory.title) {
        // articleCategory.title = articleCategory.title.replace('Research:', '');
        articleCategory.title = 'Research';
    }

    if (!blog || blog.error) {
        return <ErrorPage />;
    }

    return (
        <Shell
            page={blog}
            masterControl={masterControl}
            preview={preview}
            className="blog viome viome3"
            isLegacy={false}
        >
            <StyleContextProvider styles={styles}>
                <Head>
                    <meta property="og:type" content="article" />
                    <meta name="twitter:card" content="summary_large_image" />
                    <meta name="twitter:image:alt" content={metaDescription} />
                    <meta
                        property="article:published_time"
                        content={articleDate}
                    />
                    <meta
                        property="article:modified_time"
                        content={articleDate}
                    />
                    {articleCategory && (
                        <meta
                            property="article:section"
                            content={categoryTitle}
                        />
                    )}
                </Head>

                <ProgressBar />

                <Breadcrumb
                    items={[
                        {
                            title: 'Viome Research Blog',
                        },
                        {
                            ...articleCategory,
                        },
                    ]}
                />

                <article className={styles.blog}>
                    <BlogHeader blog={blog} />
                    <BlogBody content={blog.content} />
                    <BlogBottom blog={blog} />
                </article>
            </StyleContextProvider>
            <ArticleSchema blog={blog} />
        </Shell>
    );
};

export default Blog;

export async function getStaticProps({ params, preview = false }) {
    const masterControl = await getMasterControl.get(options),
        data = await Contentful(
            'blog',
            {
                limit: 1,
                include: 6,
                'fields.slug[in]': params.slug,
            },
            false,
            options,
        ),
        props = {
            preview,
            masterControl,
            slug: params.slug,
            blog: data,
        };

    return { props, revalidate: 60 };
}

export async function getStaticPaths() {
    const allPosts = await Contentful(
        'blog',
        { select: 'fields.slug,fields.categories', limit: 1000 },
        true,
        options,
    );
    return {
        paths: Array.isArray(allPosts)
            ? allPosts
                  .filter((post) => {
                      const articleCategories = post?.categories,
                          articleCategory =
                              articleCategories && articleCategories[0];
                      return VLS_BLOG_CATEGORIES.includes(
                          articleCategory?.slug,
                      );
                  })
                  .map((post) => {
                      const slug = post?.slug;
                      return `/blog/${slug}` ?? [];
                  })
            : [`/blog/${allPosts?.slug}`],
        fallback: 'blocking',
    };
}
